import { EMPTY_STRING } from '@modeso/aumico-lib-settings-fe';
import { ITranslationEntry } from '@modeso/types__aumico-lib-annualreport-be';
import { TranslationEditHtml } from '../../helpers/translationEditHtml.helper';
import { ITranslationMarkValue } from '../../helpers/translationMarking.helper';
import { IInsertedTranslationEntry } from '../../helpers/translationJob.helper';
import { ReportPart, UntranslatedTexts } from './UntranslatedTexts';

/** What `resolve` put into the report, keyed by the inserted text: exactly what the review marks. */
export type TranslationMarks = Map<string, ITranslationMarkValue>;

// When two sources insert the same text, the stronger origin decides the colour.
const KIND_RANK: Record<ITranslationMarkValue['kind'], number> = { plain: 0, ai: 1, user: 2 };

export abstract class GapReportingVisitor {
    private marks: TranslationMarks | undefined;

    protected constructor(
        private readonly untranslated: UntranslatedTexts | undefined,
        private readonly reportPart: ReportPart,
        private readonly translations: Record<string, ITranslationEntry> | undefined,
    ) {}

    /** Shared by all part visitors of one run, so the review marks what was really inserted. */
    recordMarksInto(marks: TranslationMarks): void {
        this.marks = marks;
    }

    protected reportGap(sourceText: unknown, part: ReportPart = this.reportPart): void {
        if (typeof sourceText !== 'string' || sourceText === EMPTY_STRING) {
            return;
        }

        this.untranslated?.add(part, sourceText);
    }

    /** The user's hand-correction for this source text, if any — wins over the fallback. Call it before
     * returning a fallback. Set by the review's correction dialog (hasUserEdited). */
    protected userOverride(source: unknown): string | undefined {
        if (typeof source !== 'string') {
            return undefined;
        }
        const entry = this.translations?.[source];
        return entry?.hasUserEdited ? entry.value : undefined;
    }

    /**
     * user override → fallback (if a real one is passed) → AI block → report the gap, keep the source.
     * Every text that comes out is editable in the review, so each branch records it with its origin:
     * a hand correction, the AI, or grey for a standard translation and for a gap.
     */
    protected resolve(source: unknown, fallback?: string, part?: ReportPart): string {
        const user = this.userOverride(source);
        if (user !== undefined) {
            return this.record(GapReportingVisitor.safe(user), source as string, 'user');
        }
        if (typeof source !== 'string') {
            return fallback ?? EMPTY_STRING;
        }
        if (fallback != null && fallback !== EMPTY_STRING) {
            return this.record(fallback, source, 'plain');
        }
        // Not only the AI block: the mapping's translation of the same text is merged in ahead of it (withMasterData).
        const entry = this.translations?.[source] as IInsertedTranslationEntry | undefined;
        if (entry) {
            return this.record(GapReportingVisitor.safe(entry.value), source, entry.fromMasterData === true ? 'plain' : 'ai');
        }
        this.reportGap(source, part);
        return this.record(source, source, 'plain');
    }

    /** Block values come from the AI or a saved correction and reach the preview past Angular's sanitizer
     * (safeHtml), so markup is cleaned like in the correction field. Plain text stays as is: it cannot run
     * code, and cleaning would escape it ("&" → "&amp;"). */
    private static safe(value: string): string {
        return TranslationEditHtml.isHtml(value) ? TranslationEditHtml.clean(value) : value;
    }

    private record(inserted: string, source: string, kind: ITranslationMarkValue['kind']): string {
        const known = this.marks?.get(inserted);
        if (inserted !== EMPTY_STRING && (known == null || KIND_RANK[kind] > KIND_RANK[known.kind])) {
            this.marks?.set(inserted, { source, kind });
        }

        return inserted;
    }
}
